import { useEffect, useMemo, useState } from 'react';
import { api, ApiError, type CalendarGrant, type ChatPerson } from '../api';
import { useApp } from '../App';
import { Avatar } from '../components/Avatar';
import { useConfirm } from '../components/Confirm';
import { MissingNote } from '../components/MissingNote';

/*
 * Личный кабинет → «Кто видит мой календарь» (задача 15).
 *
 * Занятость в календаре видна всем сотрудникам без всякой выдачи —
 * calendars.service.ts, visible(): переговорки и чужие персональные
 * календари сразу дают уровень BUSY. Здесь выдаётся только READ
 * (содержимое) и MANAGE (делегирование: тот, кому выдано, заводит встречи
 * от имени владельца) — и выдаёт их сам владелец, поимённо.
 *
 * Второй блок — секрет подписки внешнего календаря (Яндекс/Google/iPhone),
 * feed-token в адресе ленты (server/src/calendar/feed.controller.ts).
 * Сброс меняет секрет, старая ссылка отвечает 404 сразу же.
 */

const LEVELS: { value: 'READ' | 'MANAGE'; label: string }[] = [
  { value: 'READ', label: 'Чтение' },
  { value: 'MANAGE', label: 'Управление' },
];

export function CalendarAccess() {
  const app = useApp();
  const ask = useConfirm();
  const [grants, setGrants] = useState<CalendarGrant[]>([]);
  const [people, setPeople] = useState<ChatPerson[]>([]);
  const [pickId, setPickId] = useState('');
  const [pickLevel, setPickLevel] = useState<'READ' | 'MANAGE'>('READ');
  const [token, setToken] = useState('');
  const [err, setErr] = useState('');

  useEffect(() => {
    api.calGrants().then(setGrants).catch(() => setErr('Не удалось загрузить доступы'));
    api.chatPeople().then(setPeople).catch(() => {});
    api.calFeedToken().then((r) => setToken(r.token)).catch(() => {});
  }, []);

  // Кандидаты на выдачу: не сам себе, и не тем, кому уже выдано (для них —
  // смена ступени прямо в строке списка, не повторная выдача).
  const candidates = useMemo(
    () => people.filter((p) => p.id !== app.user.id && !grants.some((g) => g.userId === p.id)),
    [people, grants, app.user.id],
  );

  const grant = async () => {
    if (!pickId) return;
    setErr('');
    try {
      setGrants(await api.calGrant(pickId, pickLevel));
      setPickId('');
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : 'Не удалось выдать доступ');
    }
  };

  const changeLevel = async (userId: string, level: 'READ' | 'MANAGE') => {
    setErr('');
    try {
      setGrants(await api.calGrant(userId, level));
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : 'Не удалось изменить ступень');
    }
  };

  const revoke = async (userId: string, name: string) => {
    const ok = await ask({ title: `Снять доступ у «${name}»?`, okText: 'Снять', danger: true });
    if (!ok) return;
    setErr('');
    try {
      setGrants(await api.calRevoke(userId));
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : 'Не удалось снять доступ');
    }
  };

  const resetFeed = async () => {
    const ok = await ask({
      title: 'Сбросить ссылку подписки?',
      text: 'Старая ссылка перестанет работать сразу же — внешний календарь, подписанный по ней, отвалится.',
      okText: 'Сбросить',
      danger: true,
    });
    if (!ok) return;
    setErr('');
    try {
      const r = await api.calResetFeed();
      setToken(r.token);
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : 'Не удалось сбросить ссылку');
    }
  };

  const feedHttp = token ? `${window.location.origin}/api/calendar/feed/${token}.ics` : '';
  const feedWebcal = token ? `webcal://${window.location.host}/api/calendar/feed/${token}.ics` : '';
  const copy = (text: string) => navigator.clipboard?.writeText(text);

  return (
    <>
      <section className="card form">
        <h2>Кто видит мой календарь</h2>
        <p className="sub">
          Занятость в календаре видна всем сотрудникам без всякой выдачи. Отдельно выдаётся только чтение
          содержимого и управление — право заводить встречи от вашего имени.
        </p>
        {err && <div className="banner crit">{err}</div>}
        {/* форма выдачи стоит над списком: сюда приходят выдать доступ, а не
            смотреть выданные (замечание сотрудника 11.09.2026, П-3) */}
        <div className="form-row auto">
          <select
            className={`select md${pickId ? '' : ' missing'}`}
            aria-label="Сотрудник"
            value={pickId}
            onChange={(e) => setPickId(e.target.value)}
          >
            <option value="">выберите сотрудника</option>
            {candidates.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
              </option>
            ))}
          </select>
          <select
            className="select md"
            aria-label="Ступень"
            value={pickLevel}
            onChange={(e) => setPickLevel(e.target.value as 'READ' | 'MANAGE')}
          >
            {LEVELS.map((l) => (
              <option key={l.value} value={l.value}>
                {l.label}
              </option>
            ))}
          </select>
          <span className={pickId ? undefined : 'missing-act'}>
            <button className={`btn primary${pickId ? '' : ' missing'}`} disabled={!pickId} onClick={grant}>
              Выдать доступ
            </button>
            {!pickId && <MissingNote action />}
          </span>
        </div>
        {grants.length === 0 ? (
          <div className="empty">Доступ пока никому не выдан</div>
        ) : (
          <div className="row-list divided">
            {grants.map((g) => (
              <div key={g.userId} className="row">
                <Avatar login={g.login} name={g.name} size={32} />
                <span className="row-text">
                  <span className="row-title">{g.name}</span>
                  <span className="row-sub">@{g.login}</span>
                </span>
                <select
                  className="select sm"
                  aria-label={`Ступень доступа: ${g.name}`}
                  value={g.level}
                  onChange={(e) => changeLevel(g.userId, e.target.value as 'READ' | 'MANAGE')}
                >
                  {LEVELS.map((l) => (
                    <option key={l.value} value={l.value}>
                      {l.label}
                    </option>
                  ))}
                </select>
                <button className="btn outline sm danger" onClick={() => revoke(g.userId, g.name)}>
                  Снять
                </button>
              </div>
            ))}
          </div>
        )}
      </section>

      <section className="card form">
        <h2>Внешний календарь</h2>
        {/* пояснение стоит под заголовком, как в блоке выше: внизу оно путалось
            с содержимым и спорило кеглем со ссылками (замечание сотрудника, В-10) */}
        <p className="sub">
          Подписка обновляется не мгновенно: у Apple можно поставить обновление раз в пять минут, у Google
          задержка бывает до суток.
        </p>
        <div className="field">
          <span className="field-label">Для Яндекса и Google</span>
          <div className="form-row aside">
            <div className="tile mono cal-feed-link">{feedHttp}</div>
            <button className="btn outline" disabled={!token} onClick={() => copy(feedHttp)}>
              Скопировать
            </button>
          </div>
        </div>
        <div className="field">
          <span className="field-label">Для айфона</span>
          <div className="form-row aside">
            <div className="tile mono cal-feed-link">{feedWebcal}</div>
            <button className="btn outline" disabled={!token} onClick={() => copy(feedWebcal)}>
              Скопировать
            </button>
          </div>
          <span className="field-hint">Эта ссылка на айфоне открывает окно подписки одним касанием.</span>
        </div>
        <div className="acts">
          <button className="btn danger" onClick={resetFeed}>
            Сбросить ссылку
          </button>
        </div>
      </section>
    </>
  );
}
